// Figma design: https://www.figma.com/design/sseCNpJYq9VS8RO8p4u6HJ/Ticket-Purchase-Flow?node-id=17172-301216

import { ListAlt } from '@mui/icons-material';
import { Alert, AlertTitle, Box, Chip, Container } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { PageHeader, showToast } from '@tix-ticketing/ui-components';
import { CalendarIcon } from '@tix-ticketing/ui-icons/outline';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import type { SpecialOfferResponse } from 'api/generated';
import { postApiWaitingListConfirm } from 'api/generated';
import { SegmentedControl } from 'components/events-list/segmented-control';
import { SpecialOffersDialog } from 'components/events-list/special-offers-dialog';
import EventsCalendarPaginatedContainer from 'containers/events-list/events-calendar-paginated-container';
import EventsPaginatedContainer from 'containers/events-list/events-paginated-container';
import { FooterFullHeightContainer } from 'containers/footer/footer-fullheight-container';
import { progressBarValues } from 'lib/constants/progress.constant';
import { parseFromPath } from 'lib/helpers/query-helpers';
import { sanitizeHtml } from 'lib/helpers/string-helpers';
import { useClaimSpecialOffer } from 'lib/hooks/use-apply-special-offer';
import { getApiEventGroupsByEventGroupIdOptions } from '../../api/generated/@tanstack/react-query.gen';
import { useAppState } from '../../lib/app-state';
export default function EventGroup() {
  const appState = useAppState();
  const router = useRouter();
  const intl = useIntl();
  const eventGroupId = Number(router.query.eventGroupId);
  const [view, setView] = useState<'list' | 'calendar'>('list');

  // Special offer logic
  const [isSpecialOffersDialogOpen, setIsSpecialOffersDialogOpen] = useState(false);
  const [hasSpecialOffersDialogOpened, setHasSpecialOffersDialogOpened] = useState(false);
  const appliedSpecialOffer = useMemo(() => {
    if (appState.eventData.order?.specialOfferPassword?.specialOfferId && appState.eventData.order?.specialOfferPassword.eventGroupId === eventGroupId) {
      return {
        specialOfferId: appState.eventData.order.specialOfferPassword.specialOfferId,
        specialOfferPassword: appState.eventData.order.specialOfferPassword.password ?? undefined
      };
    }
    return null;
  }, [appState.eventData.order?.specialOfferPassword, eventGroupId]);
  const [specialOffersOnly, setSpecialOffersOnly] = useState<boolean>(!!appliedSpecialOffer);
  useEffect(() => {
    if (appliedSpecialOffer) {
      setSpecialOffersOnly(true);
    }
  }, [appliedSpecialOffer]);
  const [availableSpecialOffers, setAvailableSpecialOffers] = useState<SpecialOfferResponse[]>([]);

  // Nothing left to offer once the customer has claimed one for this group, so
  // the remaining offers stay out of the way.
  useEffect(() => {
    if (availableSpecialOffers.length > 0 && !hasSpecialOffersDialogOpened && !appliedSpecialOffer) {
      const timer = setTimeout(() => {
        setIsSpecialOffersDialogOpen(true);
        setHasSpecialOffersDialogOpened(true);
      }, 700);
      return () => clearTimeout(timer);
    }
  }, [availableSpecialOffers, hasSpecialOffersDialogOpened, appliedSpecialOffer]);
  const applySpecialOfferMutation = useClaimSpecialOffer({
    onSuccess: () => {
      setSpecialOffersOnly(true);
      setIsSpecialOffersDialogOpen(false);
    }
  });
  const handleApplySpecialOffer = useCallback((specialOfferId: number) => {
    const offer = availableSpecialOffers.find(o => o.specialOfferId === specialOfferId);
    if (!offer) {
      showToast({
        title: intl.formatMessage({
          id: "offer.applyError.title",
          defaultMessage: [{
            "type": 0,
            "value": "Cannot apply special offer"
          }]
        }),
        message: intl.formatMessage({
          id: "offer.applyError.description",
          defaultMessage: [{
            "type": 0,
            "value": "Something went wrong when trying to apply the special offer"
          }]
        }),
        severity: 'error'
      });
      return;
    }
    applySpecialOfferMutation.mutate({
      specialOfferId,
      password: appState.eventData.order?.specialOfferPassword?.specialOfferId === specialOfferId ? appState.eventData.order?.specialOfferPassword?.password : undefined
    });
  }, [availableSpecialOffers, intl, appState.eventData.order?.specialOfferPassword?.specialOfferId, appState.eventData.order?.specialOfferPassword?.password, applySpecialOfferMutation]);

  // Fetch
  const eventGroupQuery = useQuery({
    ...getApiEventGroupsByEventGroupIdOptions({
      path: {
        eventGroupId
      },
      query: {
        SpecialOfferId: appState.eventData?.order?.specialOfferPassword?.specialOfferId,
        SpecialOfferPassword: appState.eventData?.order?.specialOfferPassword?.password ?? undefined
      }
    }),
    enabled: !!eventGroupId
  });
  useEffect(() => {
    if (eventGroupQuery.data?.calendarView) {
      setView('calendar');
    }
  }, [eventGroupQuery.data]);
  useEffect(() => {
    appState.setTitle(<FormattedMessage id="event.chooseDateAndTime" defaultMessage={[{
      "type": 0,
      "value": "Choose date & time"
    }]} />);
    appState.setProgress(progressBarValues.CHECKOUT_FLOW.EVENT_GROUP);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Double opt-in landing: the waiting-list confirmation email links back here
  // with ?wlconfirm={token}. Confirm it once, toast the result, then strip the
  // param so a refresh doesn't re-submit.
  const wlHandledToken = useRef<string | null>(null);
  useEffect(() => {
    if (!router.isReady) return;
    const token = router.query.wlconfirm;
    if (typeof token !== 'string' || !token) return;
    if (wlHandledToken.current === token) return;
    wlHandledToken.current = token;
    void (async () => {
      const result = await postApiWaitingListConfirm({
        body: {
          token
        }
      });
      showToast(result.status === 200 ? {
        title: intl.formatMessage({
          id: "waitingList.confirmedTitle",
          defaultMessage: [{
            "type": 0,
            "value": "You're on the waiting list"
          }]
        }),
        message: intl.formatMessage({
          id: "waitingList.confirmedMessage",
          defaultMessage: [{
            "type": 0,
            "value": "Your waiting list signup has been confirmed. We'll be in touch if tickets become available."
          }]
        }),
        severity: 'success'
      } : {
        title: intl.formatMessage({
          id: "waitingList.confirmErrorTitle",
          defaultMessage: [{
            "type": 0,
            "value": "Confirmation failed"
          }]
        }),
        message: intl.formatMessage({
          id: "waitingList.confirmErrorMessage",
          defaultMessage: [{
            "type": 0,
            "value": "We couldn't confirm your waiting list signup. The link may have expired."
          }]
        }),
        severity: 'error'
      });
      const nextQuery = {
        ...router.query
      };
      delete nextQuery.wlconfirm;
      void router.replace({
        pathname: router.pathname,
        query: nextQuery
      }, undefined, {
        shallow: true
      });
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.isReady, router.query.wlconfirm]);

  // The halls and the date count, shown as two parts of a sectioned subtitle.
  const subtitle = useMemo(() => {
    if (!eventGroupQuery.data) return undefined;
    const values = {
      hall: eventGroupQuery.data.hallName,
      hallCount: eventGroupQuery.data.hallCount,
      eventCount: eventGroupQuery.data.eventCount
    };
    return [<FormattedMessage key="halls" id="event.groupSubtitle.halls" defaultMessage={[{
      "type": 6,
      "value": "hallCount",
      "options": {
        "one": {
          "value": [{
            "type": 1,
            "value": "hall"
          }]
        },
        "other": {
          "value": [{
            "type": 1,
            "value": "hallCount"
          }, {
            "type": 0,
            "value": " halls"
          }]
        }
      },
      "offset": 0,
      "pluralType": "cardinal"
    }]} values={values} />, <FormattedMessage key="dates" id="event.groupSubtitle.dates" defaultMessage={[{
      "type": 6,
      "value": "eventCount",
      "options": {
        "one": {
          "value": [{
            "type": 1,
            "value": "eventCount"
          }, {
            "type": 0,
            "value": " available date"
          }]
        },
        "other": {
          "value": [{
            "type": 1,
            "value": "eventCount"
          }, {
            "type": 0,
            "value": " available dates"
          }]
        }
      },
      "offset": 0,
      "pluralType": "cardinal"
    }]} values={values} />];
  }, [eventGroupQuery.data]);

  // Event group online note (configured in Box Office). Shown above the date
  // picker so it appears in both the list and calendar views.
  const noteHeading = eventGroupQuery.data?.noteHeading;
  const noteText = eventGroupQuery.data?.noteText;
  const hasNote = !!noteText && noteText.trim() !== '';

  // Back returns to an explicit `from` path when one is given (e.g. the cart's
  // upsells), otherwise home.
  const handleGoBack = useCallback(() => {
    const from = parseFromPath(router.query.from);
    if (from) {
      router.push(from);
      return;
    }
    process.env.NEXT_PUBLIC_DEV_QOL ? router.push('/') : router.push('/' /* TODO appconfig stuff? back to home? */);
  }, [router]);
  return <FooterFullHeightContainer header={<PageHeader title={eventGroupQuery.data?.name} subtitle={subtitle} loading={eventGroupQuery.isLoading} onBack={handleGoBack} backLabel={intl.formatMessage({
    id: "common.back",
    defaultMessage: [{
      "type": 0,
      "value": "Back"
    }]
  })} />}>
      <Container>
        {hasNote && <Alert severity="monochrome" action={<Chip size="small" color="default" label={<FormattedMessage id="common.action" defaultMessage={[{
        "type": 0,
        "value": "Action"
      }]} />} />} sx={{
        mb: {
          xs: 3,
          md: 4
        },
        py: 2
      }}>
            {noteHeading && <AlertTitle>{noteHeading}</AlertTitle>}
            <Box component="div" dangerouslySetInnerHTML={{
          __html: sanitizeHtml(noteText)
        }} sx={{
          '& > :last-child': {
            mb: 0
          }
        }} />
          </Alert>}
        <Box sx={{
        position: 'relative'
      }}>
          <Box sx={{
          position: 'absolute',
          top: 0,
          right: 0,
          display: {
            xs: 'none',
            md: 'inherit'
          }
        }}>
            {eventGroupQuery.data?.calendarView && <SegmentedControl options={[{
            value: 'list',
            label: <FormattedMessage id="event.listView" defaultMessage={[{
              "type": 0,
              "value": "List view"
            }]} />,
            icon: <ListAlt />
          }, {
            value: 'calendar',
            label: <FormattedMessage id="event.calendarView" defaultMessage={[{
              "type": 0,
              "value": "Calendar"
            }]} />,
            icon: <CalendarIcon />
          }]} onChange={setView} value={view} />}
          </Box>
          {view === 'list' ? <EventsPaginatedContainer eventGroupId={eventGroupId} showClearFilters={!eventGroupQuery.data?.calendarView} specialOffersOnly={specialOffersOnly} setSpecialOffersOnly={setSpecialOffersOnly} appliedSpecialOffer={appliedSpecialOffer} setAvailableSpecialOffers={setAvailableSpecialOffers} /> : <EventsCalendarPaginatedContainer eventGroupId={eventGroupId} specialOffersOnly={specialOffersOnly} setSpecialOffersOnly={setSpecialOffersOnly} appliedSpecialOffer={appliedSpecialOffer} setAvailableSpecialOffers={setAvailableSpecialOffers} />}
        </Box>
      </Container>
      <SpecialOffersDialog open={isSpecialOffersDialogOpen} onClose={() => setIsSpecialOffersDialogOpen(false)} onApplySpecialOffer={handleApplySpecialOffer} availableSpecialOffers={availableSpecialOffers} isApplying={applySpecialOfferMutation.isPending} />
    </FooterFullHeightContainer>;
}